import { RacemapColors } from '@racemap/utilities/consts/common';
import { EMPTY_FEATURE_COLLECTION } from '@racemap/utilities/consts/map';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import { buffer } from '@turf/buffer';
import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import { type FC, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, type MapMouseEvent, Source } from 'react-map-gl/mapbox';
import tinycolor from 'tinycolor2';
import { MapContext } from '../BasicMap/BasicMap';

export interface Props {
  features: Array<Feature<Point | LineString>>;
  radius: number | null;
  fillColor?: string;
  lineColor?: string;
  dashed?: boolean;
  lineWidth?: number;
  onMouseEnter?: (evt: MapMouseEvent) => void;
  onMouseLeave?: (evt: MapMouseEvent) => void;
}

export const RadiusLayer: FC<Props> = ({
  features,
  radius,
  fillColor = '#ffffff7d',
  lineColor = RacemapColors.PaleBlue,
  dashed = false,
  lineWidth = 1,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { current: id } = useRef(`radius-${shortIdBuilder()}`);
  const fColor = tinycolor(fillColor);
  const lColor = tinycolor(lineColor);
  const { setMouseEnterAction, setMouseLeaveAction, addInteractiveLayerIds } =
    useContext(MapContext);

  const featureCollection = useMemo(() => getRadiusFeatures(radius, features), [radius, features]);

  useEffect(() => {
    addInteractiveLayerIds(`layer-radius-fill-${id}`);
    addInteractiveLayerIds(`layer-radius-line-${id}`);
  }, []);

  useEffect(() => {
    if (onMouseEnter != null) {
      setMouseEnterAction(id, onMouseEnter);
    }
    if (onMouseLeave != null) {
      setMouseLeaveAction(id, onMouseLeave);
    }
  }, [onMouseEnter, onMouseLeave]);

  return (
    <Source type="geojson" data={featureCollection} id={id} key={id}>
      <Layer
        id={`layer-radius-fill-${id}`}
        key={`layer-radius-fill-${id}`}
        type="fill"
        paint={{
          'fill-color': fColor.toHexString(),
          'fill-opacity': fColor.getAlpha(),
        }}
      />
      <Layer
        id={`layer-radius-line-${id}`}
        key={`layer-radius-line-${id}`}
        type="line"
        paint={{
          'line-color': lColor.toHexString(),
          'line-dasharray': dashed ? [2, 2] : [1],
          'line-width': lineWidth,
        }}
      />
    </Source>
  );
};

const getRadiusFeatures = (
  radius: number | null,
  features: Array<Feature<Point | LineString>>,
): FeatureCollection => {
  return radius != null
    ? {
        type: 'FeatureCollection',
        features: features
          .map((f) => buffer(f, radius || 0, { units: 'meters' }))
          .filter((f) => f != null),
      }
    : EMPTY_FEATURE_COLLECTION;
};
